import { call, put, takeEvery } from 'redux-saga/effects'
import {
  FetchCustomerDocusignSigningEnvelopeOptions,
  CustomerDocusignCompleteSigning,
  CustomerDocusignDeclineSigning,
  CustomerDocusignStartSigning,
  FetchCustomerProfile,
  FetchUser,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callSecureApi } from '../api/callApi'

interface CustomerDocusignStartSigningRequest {
  signingFields?: Array<{
    tabLabel: string
    value: string
  }>
  embedded: boolean
}

function* onCustomerDocusignStartSigning(
  action: AsyncAction<CustomerDocusignStartSigningRequest, DocusignStartSigningResponse>,
) {
  try {
    const { signingFields, embedded } = action.payload || {}

    const body: Omit<CustomerDocusignStartSigningRequest, 'embedded'> = {}

    if (signingFields?.length) {
      body.signingFields = signingFields
    }

    const { payload }: { payload: DocusignStartSigningResponse } = yield call(
      callSecureApi,
      Endpoints.CustomerDocusignStartSigning,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body,
        query: {
          embedded: embedded.toString(),
        },
      },
    )

    yield put(CustomerDocusignStartSigning.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch docusign signing url'

    yield put(CustomerDocusignStartSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCustomerDocusignCompleteSigning(action: AsyncAction) {
  try {
    yield call(callSecureApi, Endpoints.CustomerDocusignCompleteSigning, {
      method: 'POST',
      apiService: ApiService.Paraworks,
    })

    yield put(CustomerDocusignCompleteSigning.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to complete signing'

    yield put(CustomerDocusignCompleteSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCustomerDocusignDeclineSigning(action: AsyncAction) {
  try {
    yield call(callSecureApi, Endpoints.CustomerDocusignDeclineSigning, {
      method: 'POST',
      apiService: ApiService.Paraworks,
    })

    yield put(CustomerDocusignDeclineSigning.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to decline signing'

    yield put(CustomerDocusignDeclineSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchSigningEnvelopeOptions(
  action: AsyncAction<void, DocusignSigningEnvelopeOptionsResponse>,
) {
  try {
    const { payload }: { payload: DocusignSigningEnvelopeOptionsResponse } = yield call(
      callSecureApi,
      Endpoints.FetchCustomerDocusignSigningEnvelopeOptions,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchCustomerDocusignSigningEnvelopeOptions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage =
      err?.payload?.message || 'Failed to fetch docusign signing envelope options'

    yield put(FetchCustomerDocusignSigningEnvelopeOptions.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* customerDocusignSaga() {
  yield takeEvery(CustomerDocusignStartSigning.type.REQUEST, onCustomerDocusignStartSigning)
  yield takeEvery(CustomerDocusignCompleteSigning.type.REQUEST, onCustomerDocusignCompleteSigning)
  yield takeEvery(CustomerDocusignDeclineSigning.type.REQUEST, onCustomerDocusignDeclineSigning)
  yield takeEvery(
    FetchCustomerDocusignSigningEnvelopeOptions.type.REQUEST,
    onFetchSigningEnvelopeOptions,
  )
}
